import { useEffect, useState } from "react";

/**
 * Credit counter for dashboards whose AI executive summary still runs on mock
 * data. The real summaries get their usage from the narrative API; these ones
 * simulate it so the panel subtitle reads the same everywhere.
 *
 * The counter ticks up once per generated summary (i.e. whenever `generationKey`
 * changes while the summary is enabled) and is persisted per dashboard so it
 * survives navigation.
 */
const MOCK_CREDIT_LIMIT = 2000;
const MOCK_CREDIT_START = 367;
const STORAGE_PREFIX = "mock_ai_summary_credits_used";

const readStoredUsed = (storageKey: string): number => {
  try {
    const raw = localStorage.getItem(storageKey);
    const parsed = raw === null ? Number.NaN : Number(raw);
    return Number.isFinite(parsed) && parsed >= 0 ? parsed : MOCK_CREDIT_START;
  } catch {
    return MOCK_CREDIT_START;
  }
};

const writeStoredUsed = (storageKey: string, used: number) => {
  try {
    localStorage.setItem(storageKey, String(used));
  } catch {
    // Storage can be unavailable (private mode, blocked site data).
  }
};

export interface MockAiSummaryUsage {
  used: number;
  limit: number;
  remaining: number;
}

export const useMockAiSummaryUsage = (
  scope: string,
  generationKey: string,
  enabled: boolean
): MockAiSummaryUsage => {
  const storageKey = `${STORAGE_PREFIX}_${scope}`;
  const [used, setUsed] = useState(() => readStoredUsed(storageKey));

  useEffect(() => {
    if (!enabled) {
      return;
    }
    setUsed(current => {
      const next = Math.min(current + 1, MOCK_CREDIT_LIMIT);
      writeStoredUsed(storageKey, next);
      return next;
    });
  }, [enabled, generationKey, storageKey]);

  return {
    used,
    limit: MOCK_CREDIT_LIMIT,
    remaining: Math.max(0, MOCK_CREDIT_LIMIT - used),
  };
};

export default useMockAiSummaryUsage;
